import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import DevicePieChart from '@/components/shared/charts/DevicePieChart'
import ShadcnHorizontalChart from '@/components/shared/charts/ShadcnHorizontalChart'
import { SegmentedControl } from '@/components/shared/layout'
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowRight01Icon } from "@hugeicons/core-free-icons"
import {
  OVERVIEW_BREAKDOWN_LIMIT,
  OVERVIEW_LIST_CLASS,
} from '@/lib/constants/analyticsOverview'
import {
  OverviewBreakdownCard,
  getPeriodUniqueVisitors,
} from '@/components/shared/overview/OverviewBreakdownCard'
import { aggregateDeviceTypes, shareOf } from '@/lib/calculations/breakdownTotals'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { ChartViewToggle, ShareDonut } from '@/components/shared/charts/ShareCharts'
import { osDisplayName } from '@/lib/utils/osDisplayName'

// Loading skeleton for pie/gauge chart (Devices view)
function PieChartSkeleton() {
  return (
    <div className="w-full flex flex-col items-center py-4">
      <svg 
        viewBox="0 0 400 220" 
        className="w-full max-w-[500px] h-auto"
        style={{ minHeight: '160px' }}
      >
        {/* Semi-circle gauge skeleton */}
        <defs>
          <linearGradient id="pieSkeletonGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.08">
              <animate attributeName="offset" values="-1;1" dur="1.8s" repeatCount="indefinite" />
            </stop>
            <stop offset="50%" stopColor="currentColor" stopOpacity="0.18">
              <animate attributeName="offset" values="-0.5;1.5" dur="1.8s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.08">
              <animate attributeName="offset" values="0;2" dur="1.8s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
        </defs>
        
        {/* Animated gauge arc segments */}
        {[0, 1, 2, 3].map((i) => {
          const startAngle = Math.PI - (i * 0.75)
          const endAngle = startAngle - 0.7
          const radius = 120
          const centerX = 200
          const centerY = 140
          
          const x1 = centerX + radius * Math.cos(startAngle)
          const y1 = centerY - radius * Math.sin(startAngle)
          const x2 = centerX + radius * Math.cos(endAngle)
          const y2 = centerY - radius * Math.sin(endAngle)
          
          return (
            <path
              key={i}
              d={`M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`}
              fill="none"
              stroke="url(#pieSkeletonGradient)"
              strokeWidth="28"
              strokeLinecap="round"
              className="text-muted-foreground"
              style={{
                opacity: 0.5 - (i * 0.08),
                animation: `pulse 2s ease-in-out infinite`,
                animationDelay: `${i * 180}ms`
              }}
            />
          )
        })}
        
        {/* Center text placeholder */}
        <rect
          x="160"
          y="115"
          width="80"
          height="24"
          rx="4"
          className="fill-muted/40 animate-pulse"
        />
        <rect
          x="172"
          y="148"
          width="56"
          height="12"
          rx="3"
          className="fill-muted/25 animate-pulse"
        />
      </svg>
      
      {/* Legend skeleton */}
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 px-4">
        {[68, 52, 44, 58].map((width, i) => (
          <div 
            key={i} 
            className="flex items-center gap-2 py-1.5 px-2"
            style={{
              animation: 'fadeInUp 0.3s ease-out forwards',
              animationDelay: `${i * 70}ms`,
              opacity: 0
            }}
          >
            <div className="size-2.5 rounded-full bg-muted/50 animate-pulse" />
            <div 
              className="h-4 rounded bg-muted/40 animate-pulse"
              style={{ width: `${width}px` }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

interface DevicesComponentProps {
  dashBoardData: any
  onOpenModal?: (modalType: string) => void
  onFilterByParam?: (category: string, filter: string) => void
  className?: string
  /** Overview: list-only muted bars, defaults to Browsers (no gauge) */
  overviewMode?: boolean
}

type TabType = 'OS' | 'Device' | 'Browser'
type TechView = 'bars' | 'donut'

const TECH_VIEW_OPTIONS = [
  { value: 'bars' as const, label: 'Ranked bars', icon: 'bars' as const },
  { value: 'donut' as const, label: 'Donut', icon: 'donut' as const },
]

const DIMENSION_NOUN: Record<TabType, [string, string]> = {
  Browser: ['browser', 'browsers'],
  OS: ['operating system', 'operating systems'],
  Device: ['device type', 'device types'],
}

export default function DevicesComponent({
  dashBoardData,
  onOpenModal,
  onFilterByParam,
  overviewMode = false,
}: DevicesComponentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const [currentTab, setCurrentTab] = useState<TabType>(overviewMode ? 'Browser' : 'OS')
  const [labels, setLabels] = useState<string[]>([])
  const [values, setValues] = useState<number[]>([])
  const [view, setView] = useState<TechView>('bars')

  // Get the base path for navigation
  const getBasePath = () => {
    if (params.workspaceId) return `/workspace/${params.workspaceId}`
    if (params.propertySlugId) return `/p/${params.propertySlugId}`
    if (params.domain) return `/d/${params.domain}`
    return ''
  }
  const basePath = getBasePath()

  useEffect(() => {
    if (!dashBoardData?.metrics?.aggregate) {
      setLabels([])
      setValues([])
      return
    }

    const aggregate = dashBoardData.metrics.aggregate
    
    if (currentTab === 'OS') {
      const osData = aggregate
        .filter((row: any) => row.client_os_aggregate === 'client_os' && row.client_os)
        .sort((a: any, b: any) => Number(b.count) - Number(a.count))
        .slice(0, overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 6)

      if (osData.length > 0) {
        setLabels(osData.map((row: any) => osDisplayName(row.client_os)))
        setValues(osData.map((row: any) => Number(row.count)))
      } else {
        setLabels([])
        setValues([])
      }
    } else if (currentTab === 'Device') {
      // `device_type` rows only, inferring from OS rows only when there are none.
      // Mixing both counted the same visitors twice.
      const deviceTypeData: Record<string, number> = Object.fromEntries(
        aggregateDeviceTypes(aggregate).map((d) => [d.name, d.count]),
      )

      const deviceArray = Object.entries(deviceTypeData)
        .sort((a: any, b: any) => b[1] - a[1])
        .slice(0, overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 5)

      if (deviceArray.length > 0) {
        setLabels(deviceArray.map(([device]) => (device as string).charAt(0).toUpperCase() + (device as string).slice(1)))
        setValues(deviceArray.map(([, count]) => Number(count)))
      } else {
        setLabels([])
        setValues([])
      }
    } else if (currentTab === 'Browser') {
      const browserData = aggregate
        .filter((row: any) => row.client_browser_aggregate === 'client_browser' && row.client_browser)
        .sort((a: any, b: any) => Number(b.count) - Number(a.count))
        .slice(0, overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 6)

      if (browserData.length > 0) {
        setLabels(browserData.map((row: any) => row.client_browser || 'Unknown'))
        setValues(browserData.map((row: any) => Number(row.count)))
      } else {
        setLabels([])
        setValues([])
      }
    }
  }, [dashBoardData, currentTab, overviewMode])

  // Navigate to the full Devices page instead of opening a modal
  const navigateToDevices = () => {
    navigate(`${basePath}/devices${location.search}`)
  }

  const handleFilter = (category: string, title: string) => {
    if (onFilterByParam) {
      onFilterByParam(category, title)
    }
  }

  const tabOrder: { value: TabType; label: string }[] = overviewMode
    ? [
        { value: 'Browser', label: 'Browsers' },
        { value: 'OS', label: 'OS' },
        { value: 'Device', label: 'Devices' },
      ]
    : [
        { value: 'OS', label: 'OS' },
        { value: 'Device', label: 'Devices' },
        { value: 'Browser', label: 'Browsers' },
      ]

  if (overviewMode) {
    // Donut shares divide by the period's deduped total, same as the bars. The
    // rows not charted (undetected, or past the top rows) stay the empty track.
    const periodTotal = getPeriodUniqueVisitors(dashBoardData?.metrics)
    const charted = values.reduce((sum, v) => sum + (v || 0), 0)
    const [one, many] = DIMENSION_NOUN[currentTab]
    const donut =
      view === 'donut' && labels.length > 0 ? (
        <ShareDonut
          slices={labels.map((label, i) => ({
            key: label,
            label,
            value: values[i] ?? 0,
            share: periodTotal > 0 ? shareOf(values[i] ?? 0, periodTotal) : null,
          }))}
          total={periodTotal}
          totalLabel="unique visitors"
          coverage={`Across ${labels.length} ${labels.length === 1 ? one : many} · ${formatNumeric(charted)} of ${formatNumeric(periodTotal)} unique visitors`}
          onSliceClick={(label) => handleFilter(currentTab, label)}
        />
      ) : undefined
    return (
      <OverviewBreakdownCard
        headerAction={<ChartViewToggle options={TECH_VIEW_OPTIONS} value={view} onChange={setView} />}
        alternateBody={donut}
        title="Technology"
        tabs={tabOrder}
        tab={currentTab}
        onTabChange={setCurrentTab}
        dimensionLabel={currentTab === 'Browser' ? 'Browser' : currentTab === 'OS' ? 'OS' : 'Device'}
        valueLabel="Visitors"
        showColumnHeader={false}
        rows={labels.map((label, i) => ({ label, value: values[i] ?? 0 }))}
        total={getPeriodUniqueVisitors(dashBoardData?.metrics)}
        limit={4}
        isLoading={!dashBoardData}
        onRowClick={(label) => handleFilter(currentTab, label)}
        footerLabel="View all technology"
        onFooterClick={navigateToDevices}
      />
    )
  }

  return (
    <Card>
      <CardHeader className="border-b border-border/40 pb-3">
        <div className="flex items-center justify-between gap-3 w-full">
          {overviewMode && (
            <span className="text-sm font-medium text-foreground shrink-0">Technology</span>
          )}
          <SegmentedControl
            options={tabOrder}
            value={currentTab}
            onChange={setCurrentTab}
            className="flex-1 min-w-0"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={navigateToDevices}
          >
            View all
            <HugeiconsIcon icon={ArrowRight01Icon} size={14} className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {!dashBoardData ? (
          overviewMode ? (
            <div className="space-y-2 py-2">
              {[0.9, 0.65, 0.5, 0.35, 0.25].map((w, i) => (
                <div key={i} className="h-8 rounded-sm bg-muted/40 animate-pulse" style={{ width: `${w * 100}%` }} />
              ))}
            </div>
          ) : (
            <PieChartSkeleton />
          )
        ) : labels.length === 0 ? (
          <div className="dashboard-empty">
            No data to display. Try a different date range.
          </div>
        ) : overviewMode ? (
          <div className={OVERVIEW_LIST_CLASS}>
            <ShadcnHorizontalChart
              labels={labels}
              values={values}
              maxItems={OVERVIEW_BREAKDOWN_LIMIT}
              onBarClick={(label) => handleFilter(currentTab, label)}
              title={currentTab === 'Browser' ? 'Browser' : currentTab === 'OS' ? 'OS' : 'Device'}
              valueLabel="Visitors"
              showFavicons={false}
              variant="underline"
            />
          </div>
        ) : (
          <div className="w-full flex-1 overflow-y-auto min-h-0">
            <DevicePieChart
              labels={labels}
              values={values}
              type={currentTab as 'OS' | 'Device' | 'Browser'}
              onSegmentClick={(label) => handleFilter(currentTab, label)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
